import { useEffect, useRef, useState } from 'react';
import { Meteor } from 'meteor/meteor';
import {
  clearLegacyScopeGroups,
  hasLegacyScopeGroups,
  readLegacyScopeGroups,
} from '../../../lib/clientConfig';

export type ScopeGroupsMoveResult = {
  imported: number;
  skipped: number;
  droppedVenues: number;
};

/**
 * Moves the venue groups this browser kept in localStorage to the server,
 * once, as the person's own (decision 7.7.2), then forgets them here. The
 * server keeps each group's id (saved scopes and links that name it keep
 * working), skips the ones the person already has (a second tab, a second
 * try) and leaves out venues they can no longer open.
 *
 * Runs when the person and the restaurant are known (`ready`). A failed
 * move keeps the groups here and is tried again on the next page load.
 */
export function useScopeGroupsMove({
  restaurantId,
  ownerId,
  ready,
  onMoved,
}: {
  restaurantId: string;
  ownerId: string | null;
  ready: boolean;
  onMoved?: (result: ScopeGroupsMoveResult) => void | Promise<unknown>;
}): ScopeGroupsMoveResult | null {
  const started = useRef(false);
  const onMovedRef = useRef(onMoved);
  onMovedRef.current = onMoved;
  const [result, setResult] = useState<ScopeGroupsMoveResult | null>(null);

  useEffect(() => {
    if (!ready || !ownerId || !restaurantId || started.current) return;
    if (!hasLegacyScopeGroups()) return;
    started.current = true;
    const groups = readLegacyScopeGroups();
    if (!groups.length) {
      // Only the multi-select's scratch group (or nothing usable) was left.
      clearLegacyScopeGroups();
      return;
    }
    void Meteor.callPromise<ScopeGroupsMoveResult>('opsCommand/importScopeGroups', restaurantId, groups)
      .then(async moved => {
        clearLegacyScopeGroups();
        setResult(moved);
        await onMovedRef.current?.(moved);
      })
      .catch(() => {
        // Kept here; the next page load tries again.
      });
  }, [ready, ownerId, restaurantId]);

  return result;
}
